import { useState } from "react";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Header } from "../../components/Header";
import { useGetData } from "../../Network/useGetQueryData";
import { Loading } from "../../components/Loading";
import { UserRolesId } from "../enum";
import "./style.scss";

interface MonthlyReport {
  month: string;
  totalRequests: number;
  completedRequests: number;
  completionRate: number;
  totalAmount: number;
  medHubShare: number;
  customerShare: number;
  coverageAmount: number;
  avgCostPerRequest: number;
  newMemberships: number;
  topServices: { id: number; name: { en?: string; ar?: string }; count: number }[];
  dailyRequestCounts: { date: string; count: number }[];
  providerClaims: {
    id: number;
    name: { en?: string; ar?: string };
    claimAmount: number;
    requestCount: number;
  }[];
}

const ReportsPage = () => {
  const { t, i18n } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.user);
  const isAdmin = userInfo?.roleId === UserRolesId.Admin;
  const [month, setMonth] = useState<Dayjs>(dayjs());

  const { data, isLoading } = useGetData<{ data: MonthlyReport }>({
    queryKey: ["monthly-report", { month: month.format("YYYY-MM") }],
    url: "/report/monthly",
    params: { month: month.format("YYYY-MM") },
  });

  const report = data?.data;

  const chartData =
    report?.dailyRequestCounts.map((d) => ({
      date: dayjs(d.date).format("DD/MM"),
      count: d.count,
    })) || [];

  const currency = t("reports.currency");
  const amount = (value?: number) => `${(value ?? 0).toLocaleString()} ${currency}`;

  return (
    <>
      <Header
        title={
          userInfo?.roleId === UserRolesId.Sponsor
            ? t("reports.header.titleSponsor")
            : t("reports.header.titleAdmin")
        }
        headerFilter={
          <DatePicker
            picker="month"
            value={month}
            allowClear={false}
            onChange={(value) => value && setMonth(value)}
          />
        }
      />

      <div className="reports-content">
        {isLoading && !report ? (
          <Loading />
        ) : (
          <>
            <div className="reports-stats-grid">
              <div className="reports-stat-card">
                <h3>{report?.totalRequests ?? 0}</h3>
                <p>{t("reports.stats.totalRequests")}</p>
              </div>
              <div className="reports-stat-card">
                <h3>{report?.completionRate ?? 0}%</h3>
                <p>{t("reports.stats.completionRate")}</p>
              </div>
              <div className="reports-stat-card">
                <h3>{report?.newMemberships ?? 0}</h3>
                <p>{t("reports.stats.newMemberships")}</p>
              </div>
            </div>

            <div className="reports-section-card">
              <h4>{t("reports.amounts.title")}</h4>
              <div className="reports-stats-grid">
                <div className="reports-stat-card">
                  <h3>{amount(report?.totalAmount)}</h3>
                  <p>{t("reports.amounts.total")}</p>
                </div>
                <div className="reports-stat-card">
                  <h3>{amount(report?.customerShare)}</h3>
                  <p>{t("reports.amounts.customerShare")}</p>
                </div>
                <div className="reports-stat-card">
                  <h3>{amount(report?.coverageAmount)}</h3>
                  <p>{t("reports.amounts.coverageAmount")}</p>
                </div>
                {isAdmin && (
                  <div className="reports-stat-card">
                    <h3>{amount(report?.medHubShare)}</h3>
                    <p>{t("reports.amounts.medHubShare")}</p>
                  </div>
                )}
                <div className="reports-stat-card">
                  <h3>{amount(report?.avgCostPerRequest)}</h3>
                  <p>{t("reports.stats.avgCost")}</p>
                </div>
              </div>
            </div>

            <div className="reports-section-card">
              <h4>{t("reports.chart.title")}</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="rgb(var(--primary-color))" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="reports-section-card">
              <h4>{t("reports.topServices.title")}</h4>
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>{t("reports.topServices.service")}</th>
                    <th>{t("reports.topServices.count")}</th>
                  </tr>
                </thead>
                <tbody>
                  {report?.topServices?.length ? (
                    report.topServices.map((service) => (
                      <tr key={service.id}>
                        <td>{service.name?.[i18n.language] || service.name?.en}</td>
                        <td>{service.count}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2}>{t("reports.topServices.empty")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {isAdmin && (
              <div className="reports-section-card">
                <h4>{t("reports.providerClaims.title")}</h4>
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>{t("reports.providerClaims.provider")}</th>
                      <th>{t("reports.providerClaims.requests")}</th>
                      <th>{t("reports.providerClaims.claimAmount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report?.providerClaims?.length ? (
                      report.providerClaims.map((provider) => (
                        <tr key={provider.id}>
                          <td>{provider.name?.[i18n.language] || provider.name?.en}</td>
                          <td>{provider.requestCount}</td>
                          <td>{amount(provider.claimAmount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3}>{t("reports.providerClaims.empty")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ReportsPage;
